import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    Link,
} from '@mui/material'
import UserIcon from '@mui/icons-material/Person'
import SecurePasswordIcon from '@mui/icons-material/Https'
import ShowPasswordIcon from '@mui/icons-material/Visibility'
import HidePasswordIcon from '@mui/icons-material/VisibilityOff'
import React, { FormEvent, useState } from 'react'
import { IconButton, TextField, FormHelperText } from './LoginForm.styled'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './LoginForm.messages'
import ApiError from '../../entities/api-error/ApiError'

export interface IOwnProps {}

export interface IStateProps {
    isRequesting: boolean
    error?: ApiError
}

export interface IDispatchProps {
    requestLogin: (email: string, password: string) => void
}

export type IProps = IOwnProps & IStateProps & IDispatchProps

interface IField {
    value: string
}

const LoginForm: React.FC<IProps> = (props) => {
    const { requestLogin, isRequesting, error: apiError } = props
    const intl = useIntl()

    const [shouldShowPassword, setShouldShowPassword] = useState(false)
    const [email, setEmail] = useState<IField>({
        value: '',
    })
    const [password, setPassword] = useState<IField>({
        value: '',
    })

    const handleClickShowPassword = () => {
        setShouldShowPassword(!shouldShowPassword)
    }

    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault()
    }

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail({
            ...email,
            value: event.target.value,
        })
    }

    const handlePasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setPassword({
            ...password,
            value: event.target.value,
        })
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        requestLogin(email.value, password.value)
    }

    return (
        <form onSubmit={handleSubmit}>
            <Box mb={10}>
                <TextField
                    type='email'
                    placeholder={intl.formatMessage({ ...messages.email })}
                    variant='outlined'
                    autoComplete='email'
                    value={email.value}
                    required
                    onChange={handleEmailChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                position='start'
                                disablePointerEvents
                            >
                                <UserIcon />
                            </InputAdornment>
                        ),
                    }}
                    autoFocus
                    fullWidth
                />
            </Box>

            <Box mb={'130px'}>
                <TextField
                    type={shouldShowPassword ? 'text' : 'password'}
                    placeholder={intl.formatMessage({ ...messages.password })}
                    variant='outlined'
                    autoComplete='current-password'
                    value={password.value}
                    required
                    onChange={handlePasswordChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                position='start'
                                disablePointerEvents
                            >
                                <SecurePasswordIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position='end'>
                                <IconButton
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge='end'
                                >
                                    {shouldShowPassword ? (
                                        <HidePasswordIcon />
                                    ) : (
                                        <ShowPasswordIcon />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    fullWidth
                />
            </Box>

            {/* <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '130px',

                    '@media (max-width: 768px)': {
                        marginBottom: '50px',
                    },
                }}
            >
                <Link
                    color='#7d7d7d'
                    fontSize='14px'
                    href={process.env.REACT_APP_EXTERNAL_LINK || '/'}
                >
                    {messages.forgotPasswordMessage.defaultMessage}
                </Link>
            </Box> */}

            <Box>
                <Button
                    type='submit'
                    color='primary'
                    variant='contained'
                    disabled={isRequesting}
                    fullWidth
                >
                    {isRequesting ? (
                        <CircularProgress size={24} />
                    ) : (
                        <FormattedMessage {...messages.logIn} />
                    )}
                </Button>

                {apiError ? (
                    <FormHelperText error>
                        <FormattedMessage {...messages.incorrectForm} />
                    </FormHelperText>
                ) : null}
            </Box>
        </form>
    )
}

export default LoginForm
